import { UiProps, WalletConnectionStatus } from '@noahsaso/cosmodal'
import { isAndroid, isMobile } from '@walletconnect/browser-utils'
import clsx from 'clsx'
import Link from 'next/link'
import QRCode from 'qrcode.react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@dao-dao/stateless'

import { Trans } from '../Trans'

const IOS_KEPLR_MOBILE_URL = 'itms-apps://itunes.apple.com/app/1567851089'

export const CosmodalWalletConnectQr = ({
  walletConnectUri,
  reset,
  status,
}: UiProps) => {
  const { t } = useTranslation()

  const mobile = isMobile()
  const android = isAndroid()

  // Defined if wallet connect URI exists and this is a mobile device..
  const navigateToMobileWalletConnectUrl =
    walletConnectUri &&
    (mobile
      ? android
        ? `intent://wcV1?${walletConnectUri}#Intent;package=com.chainapsis.keplr;scheme=keplrwallet;end;`
        : `keplrwallet://wcV1?${walletConnectUri}`
      : undefined)

  // Open app if mobile URL is available.
  useEffect(() => {
    if (
      status !== WalletConnectionStatus.Connecting ||
      !navigateToMobileWalletConnectUrl
    ) {
      return
    }

    // Slight delay so they can read the modal.
    const timeout = setTimeout(() => {
      window.location.href = navigateToMobileWalletConnectUrl
    }, 1000)

    return () => clearTimeout(timeout)
  }, [navigateToMobileWalletConnectUrl, status])

  const [qrShowing, setQrShowing] = useState(!mobile)

  // Show mobile help if timeout is reached.
  const [showMobileHelp, setShowMobileHelp] = useState(false)
  useEffect(() => {
    if (!mobile || status !== WalletConnectionStatus.Connecting) {
      setShowMobileHelp(false)
      return
    }

    const timeout = setTimeout(() => setShowMobileHelp(true), 5000)
    return () => clearTimeout(timeout)
  }, [status, mobile])

  return (
    <>
      {!!navigateToMobileWalletConnectUrl && (
        <div className={clsx(qrShowing && 'mb-4')}>
          <p className="mb-4">
            <Trans i18nKey="info.openMobileWalletAndAcceptConnection">
              <Link
                className="underline"
                href={navigateToMobileWalletConnectUrl}
              >
                Open your mobile wallet
              </Link>{' '}
              and accept the connection request.
            </Trans>
          </p>

          <p className={clsx(showMobileHelp ? 'mb-4' : 'mb-6')}>
            <Trans i18nKey="info.installKeplrMobileOrScanQrCode">
              If you don&apos;t have Keplr Mobile installed,{' '}
              <Link
                className="underline"
                href={
                  android
                    ? navigateToMobileWalletConnectUrl
                    : IOS_KEPLR_MOBILE_URL
                }
              >
                click here to install it
              </Link>{' '}
              or scan the QR code at the bottom with another device.
            </Trans>
          </p>

          {showMobileHelp && (
            <p className="mb-6">
              <Trans i18nKey="info.keplrMobileReset">
                If nothing shows up in Keplr Mobile,{' '}
                <Button className="inline" onClick={reset} variant="underline">
                  click here to reset
                </Button>{' '}
                and try connecting again. Refresh the page if the problem
                persists.
              </Trans>
            </p>
          )}

          <Button onClick={() => setQrShowing((s) => !s)} variant="underline">
            {qrShowing ? t('button.hideQrCode') : t('button.showQrCode')}
          </Button>
        </div>
      )}

      {qrShowing && !!walletConnectUri && (
        <QRCode
          size={500}
          style={{ width: '100%', height: '100%' }}
          value={walletConnectUri}
        />
      )}
    </>
  )
}
